import { create } from 'zustand';
import { ContactData, SocialMedia } from '../dto/EntityRepository';
import type { ResourceScope, ResourceStatus } from '../util/scopedResource';

interface ContactState {
  contact: ContactData | null;
  contactOwnerAui: string | null;
  contactScope: ResourceScope | null;
  status: ResourceStatus;
  hasChanged: boolean;
  setContact: (contact: ContactData, ownerAui: string, scope: ResourceScope) => void;
  beginContactFetch: (ownerAui: string, scope: ResourceScope) => void;
  setContactError: () => void;
  updateContact: (updates: Partial<ContactData>) => void;
  updateSns: (updates: Partial<SocialMedia>) => void;
  clearContact: () => void;
}

const isSameContact = (prev: ContactData | null, next: ContactData): boolean => {
  if (!prev) return false;
  return (
    prev.id === next.id &&
    prev.address === next.address &&
    prev.email === next.email &&
    prev.contact === next.contact &&
    prev.sns.twitter === next.sns.twitter &&
    prev.sns.instagram === next.sns.instagram &&
    prev.sns.facebook === next.sns.facebook &&
    prev.sns.threads === next.sns.threads &&
    prev.sns.behance === next.sns.behance &&
    prev.sns.youtube === next.sns.youtube &&
    prev.sns.vimeo === next.sns.vimeo &&
    prev.sns.url1 === next.sns.url1
  );
};

export const useContactStore = create<ContactState>((set) => ({
  contact: null,
  contactOwnerAui: null,
  contactScope: null,
  status: 'idle',
  hasChanged: false,
  beginContactFetch: (ownerAui, scope) => set((state) => {
    // 같은 owner+scope 재fetch: success 유지(SWR). 다르면 loading으로 숨김.
    if (
      state.contactOwnerAui === ownerAui &&
      state.contactScope === scope &&
      state.status === 'success'
    ) {
      return state;
    }
    return { status: 'loading' };
  }),
  setContact: (contact, ownerAui, scope) =>
    set((state) => {
      if (
        state.status === 'success' &&
        state.contactOwnerAui === ownerAui &&
        state.contactScope === scope &&
        isSameContact(state.contact, contact)
      ) {
        return state;
      }
      return {
        contact,
        contactOwnerAui: ownerAui,
        contactScope: scope,
        status: 'success',
        hasChanged: false,
      };
    }),
  setContactError: () => set({ status: 'error' }),
  updateContact: (updates) =>
    set(({ contact }) => ({
      contact: contact ?
        { ...contact, ...updates }
        : null,
      hasChanged: true
    })),
  updateSns: (updates) =>
    set(({ contact }) => ({
      contact: contact ? {
        ...contact,
        sns: {
          ...contact.sns,
          ...updates
        }
      } : null,
      hasChanged: true,
    })),
  clearContact: () => set((state) => {
    if (
      state.contact === null &&
      state.contactOwnerAui === null &&
      state.contactScope === null &&
      state.status === 'idle' &&
      state.hasChanged === false
    ) {
      return state;
    }
    return {
      contact: null,
      contactOwnerAui: null,
      contactScope: null,
      status: 'idle',
      hasChanged: false,
    };
  }),
}));
